import { useState } from 'react';
import { ChevronDown, ChevronLeft } from 'lucide-react';
import * as Icons from 'lucide-react';

interface Topic {
  id: string;
  title: string;
}

interface NavItemProps {
  item: { id: string; title: string; icon: string; topics?: Topic[] };
  isActive: boolean;
  currentTopicId: string | null;
  onSectionClick: () => void;
  onTopicClick: (topicId: string) => void;
  expandedItems: string[];
  onToggleExpand: (id: string) => void;
}

function getIcon(name: string) {
  return (Icons as any)[name] || Icons.FileText;
}

export default function NavItem({
  item,
  isActive,
  currentTopicId,
  onSectionClick,
  onTopicClick,
  expandedItems,
  onToggleExpand,
}: NavItemProps) {
  const Icon = getIcon(item.icon);
  const isExpanded = expandedItems.includes(item.id);
  const hasTopics = item.topics && item.topics.length > 0;
  const [hovered, setHovered] = useState(false);

  return (
    <div>
      <button
        onClick={() => {
          onSectionClick();
          if (hasTopics) onToggleExpand(item.id);
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={`flex items-center gap-2.5 w-full py-2.5 px-3 rounded-lg cursor-pointer text-left transition-all duration-150 ${
          isActive && !currentTopicId
            ? 'bg-oak-green/[0.15] border-r-[3px] border-r-oak-green'
            : 'border-r-[3px] border-r-transparent'
        } ${hovered && !(isActive && !currentTopicId) ? 'bg-surface-light' : ''}`}
      >
        <Icon
          size={18}
          className={isActive ? 'text-oak-green' : 'text-content-secondary'}
        />
        <span
          className={`text-sm flex-1 ${
            isActive ? 'text-oak-green font-semibold' : 'text-content-primary font-normal'
          }`}
        >
          {item.title}
        </span>
        {hasTopics &&
          (isExpanded ? (
            <ChevronDown size={16} className="text-content-secondary" />
          ) : (
            <ChevronLeft size={16} className="text-content-secondary" />
          ))}
      </button>

      {hasTopics && isExpanded && (
        <div className="mr-3 mt-1 mb-2 pr-3">
          {item.topics!.map((topic) => (
            <TopicItem
              key={topic.id}
              topic={topic}
              isActive={currentTopicId === topic.id}
              onClick={() => onTopicClick(topic.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TopicItem({
  topic,
  isActive,
  onClick,
}: {
  topic: Topic;
  isActive: boolean;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`block w-full py-2 px-3 rounded-md cursor-pointer text-left text-[13px] transition-all duration-150 ${
        isActive
          ? 'bg-oak-green/10 text-oak-green font-semibold'
          : hovered
            ? 'bg-surface-light text-content-secondary'
            : 'text-content-secondary font-normal'
      }`}
    >
      <span className="text-content-secondary mr-1.5">{topic.id}</span>
      {topic.title}
    </button>
  );
}
